import { WebsiteSettings } from './settingsService';
import { Service } from '../types';

/**
 * Service to handle SEO operations
 */
export const seoService = {
  /**
   * Generate meta tags for the home page
   */
  getHomeMetaTags(settings: WebsiteSettings) {
    return {
      title: settings.seo.title,
      description: settings.seo.description,
      keywords: settings.seo.keywords,
      canonical: window.location.origin,
      type: 'website',
      image: settings.general.logoUrl || '',
    };
  },

  /**
   * Generate meta tags for the about page
   */
  getAboutMetaTags(settings: WebsiteSettings) {
    return {
      title: `About Us | ${settings.seo.title}`,
      description: 'Learn more about Paragon Live Design, our team, values and mission.',
      keywords: `about, team, values, mission, ${settings.seo.keywords}`,
      canonical: `${window.location.origin}/about`,
      type: 'website',
      image: settings.general.logoUrl || '',
    };
  },

  /**
   * Generate meta tags for the services page
   */
  getServicesMetaTags(settings: WebsiteSettings, service?: Service) {
    if (service) {
      // Individual service page
      return {
        title: `${service.title} | ${settings.seo.title}`,
        description: service.description,
        keywords: `${service.title}, ${service.category}, ${settings.seo.keywords}`,
        canonical: `${window.location.origin}/services/${service.slug}`,
        type: 'website',
        image: service.images.length > 0 ? service.images[service.featuredImageIndex || 0] : settings.general.logoUrl,
      };
    }

    // Services list page
    return {
      title: `Services | ${settings.seo.title}`,
      description: 'Explore our professional services including production design, consulting, drafting, and more.',
      keywords: `services, production design, consulting, drafting, ${settings.seo.keywords}`,
      canonical: `${window.location.origin}/services`,
      type: 'website',
      image: settings.general.logoUrl || '',
    };
  },

  /**
   * Generate meta tags for the portfolio page
   */
  getPortfolioMetaTags(settings: WebsiteSettings, projectTitle?: string, projectDescription?: string) {
    if (projectTitle) {
      // Individual project page
      return {
        title: `${projectTitle} | Portfolio | ${settings.seo.title}`,
        description: projectDescription || `View our ${projectTitle} project details and photos.`,
        keywords: `portfolio, project, ${projectTitle}, ${settings.seo.keywords}`,
        canonical: `${window.location.origin}/portfolio/${projectTitle.toLowerCase().replace(/\s+/g, '-')}`,
        type: 'article',
        image: settings.general.logoUrl || '',
      };
    }

    // Portfolio list page
    return {
      title: `Portfolio | ${settings.seo.title}`,
      description: 'Explore our portfolio of successful projects and client work.',
      keywords: `portfolio, projects, work, ${settings.seo.keywords}`,
      canonical: `${window.location.origin}/portfolio`,
      type: 'website',
      image: settings.general.logoUrl || '',
    };
  },

  /**
   * Generate meta tags for the tools page
   */
  getToolsMetaTags(settings: WebsiteSettings, toolName?: string) {
    if (toolName) {
      // Individual tool page
      return {
        title: `${toolName} | Tools | ${settings.seo.title}`,
        description: `Use our ${toolName} tool for professional production planning and design.`,
        keywords: `tools, ${toolName}, production planning, ${settings.seo.keywords}`,
        canonical: `${window.location.origin}/tools/${toolName.toLowerCase().replace(/\s+/g, '-')}`,
        type: 'website',
        image: settings.general.logoUrl || '',
      };
    }

    // Tools list page
    return {
      title: `Tools | ${settings.seo.title}`,
      description: 'Access our suite of professional production design and planning tools.',
      keywords: `tools, calculators, planning, production design, ${settings.seo.keywords}`,
      canonical: `${window.location.origin}/tools`,
      type: 'website',
      image: settings.general.logoUrl || '',
    };
  },

  /**
   * Generate meta tags for the contact page
   */
  getContactMetaTags(settings: WebsiteSettings) {
    return {
      title: `Contact Us | ${settings.seo.title}`,
      description: 'Contact Paragon Live Design for professional production design and consulting services.',
      keywords: `contact, email, phone, inquiry, ${settings.seo.keywords}`,
      canonical: `${window.location.origin}/contact`,
      type: 'website',
      image: settings.general.logoUrl || '',
    };
  },

  /**
   * Generate structured data JSON-LD for the organization
   */
  getOrganizationStructuredData(settings: WebsiteSettings) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Paragon Live Design',
      url: window.location.origin,
      logo: settings.general.logoUrl,
      description: settings.seo.description,
      sameAs: [
        settings.social.facebook,
        settings.social.instagram,
        settings.social.linkedin,
        settings.social.youtube,
      ].filter(Boolean), // Filter out empty URLs
    };
  },

  /**
   * Generate structured data JSON-LD for a service
   */
  getServiceStructuredData(service: Service, settings: WebsiteSettings) {
    return {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: service.title,
      description: service.description,
      provider: {
        '@type': 'Organization',
        name: 'Paragon Live Design',
        url: window.location.origin,
      },
      serviceType: service.category,
      url: `${window.location.origin}/services/${service.slug}`,
      image: service.images.length > 0 ? service.images[service.featuredImageIndex || 0] : settings.general.logoUrl,
    };
  },

  /**
   * Generate a sitemap XML string
   */
  generateSitemap(services: Service[], settings: WebsiteSettings): string {
    const baseUrl = window.location.origin;
    const now = new Date().toISOString();
    
    // Define standard pages
    const standardPages = [
      { url: '/', priority: '1.0', changefreq: 'weekly' },
      { url: '/about', priority: '0.8', changefreq: 'monthly' },
      { url: '/services', priority: '0.8', changefreq: 'weekly' },
      { url: '/portfolio', priority: '0.8', changefreq: 'weekly' },
      { url: '/tools', priority: '0.8', changefreq: 'monthly' },
      { url: '/contact', priority: '0.7', changefreq: 'monthly' },
    ];
    
    // Filter out hidden pages
    const visiblePages = standardPages.filter(page => {
      const pageKey = page.url === '/' ? 'home' : page.url.slice(1);
      return settings.pageVisibility[pageKey as keyof typeof settings.pageVisibility] !== false;
    });
    
    // Add service detail pages
    const servicePages = services.map(service => ({
      url: `/services/${service.slug}`,
      priority: '0.7',
      changefreq: 'monthly',
      lastmod: service.updatedAt.toISOString()
    }));
    
    // Add individual tool pages
    const toolPages = [];
    if (settings.pageVisibility.tools) {
      // Add enabled tools from settings
      for (const [toolId, toolConfig] of Object.entries(settings.tools)) {
        if (toolConfig.enabled !== false) {
          toolPages.push({
            url: `/tools/${toolId}`,
            priority: '0.7',
            changefreq: 'monthly'
          });
        }
      }
    }
    
    // Combine all pages
    const allPages = [...visiblePages, ...servicePages, ...toolPages];
    
    // Generate XML
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
    
    // Add each URL
    allPages.forEach(page => {
      xml += '  <url>\n';
      xml += `    <loc>${baseUrl}${page.url}</loc>\n`;
      if ('lastmod' in page) {
        xml += `    <lastmod>${page.lastmod}</lastmod>\n`;
      } else {
        xml += `    <lastmod>${now}</lastmod>\n`;
      }
      xml += `    <changefreq>${page.changefreq}</changefreq>\n`;
      xml += `    <priority>${page.priority}</priority>\n`;
      xml += '  </url>\n';
    });
    
    xml += '</urlset>';
    
    return xml;
  }
}; 